import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from './firebase';

/**
 * Migrate user stats from old userStats collection to users collection
 */
export const migrateUserStats = async (userId: string): Promise<void> => {
  try {
    console.log('🔄 Starting stats migration for user:', userId);

    // Get old stats from userStats collection
    const oldStatsRef = doc(db, 'userStats', userId);
    const oldStatsDoc = await getDoc(oldStatsRef);

    if (!oldStatsDoc.exists()) {
      console.log('ℹ️ No old stats found in userStats collection');
      return;
    }

    const oldStats = oldStatsDoc.data();
    console.log('📦 Old stats found:', oldStats);

    // Get user document
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      console.error('❌ User document not found');
      return;
    }

    const userData = userDoc.data();

    // Check if stats already exist in user document
    if (userData.stats && userData.stats.gamesPlayed > 0) {
      console.log('ℹ️ Stats already exist in user document, checking if XP needs backfill');

      // Check if XP needs to be backfilled from historical stats
      const currentXP = userData.xp || 0;
      if (currentXP === 0 && (userData.stats.totalScore || oldStats.totalScore)) {
        const totalScore = userData.stats.totalScore || oldStats.totalScore || 0;
        const backfilledXP = Math.round(totalScore / 10);

        // Calculate rank based on backfilled XP
        let newRank = 'Seeker';
        if (backfilledXP >= 50000) newRank = 'Master';
        else if (backfilledXP >= 15000) newRank = 'Prophet';
        else if (backfilledXP >= 5000) newRank = 'Scholar';
        else if (backfilledXP >= 1000) newRank = 'Disciple';

        await updateDoc(userRef, {
          xp: backfilledXP,
          rank: newRank,
        });

        console.log(`✅ Backfilled ${backfilledXP} XP from historical totalScore of ${totalScore}`);
        console.log(`🏆 Updated rank to: ${newRank}`);
      }

      return;
    }

    // Calculate XP from historical total score
    const totalScore = oldStats.totalScore || 0;
    const historicalXP = Math.round(totalScore / 10);

    // Calculate rank based on XP
    let newRank = 'Seeker';
    if (historicalXP >= 50000) newRank = 'Master';
    else if (historicalXP >= 15000) newRank = 'Prophet';
    else if (historicalXP >= 5000) newRank = 'Scholar';
    else if (historicalXP >= 1000) newRank = 'Disciple';

    // Migrate stats to user document with calculated XP
    await updateDoc(userRef, {
      'stats.gamesPlayed': oldStats.gamesPlayed || 0,
      'stats.totalScore': oldStats.totalScore || 0,
      'stats.averageScore': oldStats.averageScore || 0,
      'stats.bestScore': oldStats.bestScore || 0,
      'stats.longestStreak': oldStats.longestStreak || 0,
      'stats.accuracyRate': oldStats.accuracyRate || 0,
      'stats.favoriteCategory': oldStats.favoriteCategory || null,
      'stats.currentStreak': oldStats.currentStreak || 0,
      'stats.gamesWon': oldStats.gamesWon || 0,
      xp: historicalXP,
      rank: newRank,
    });

    console.log(`✅ Stats migration completed successfully!`);
    console.log(`📊 Migrated ${oldStats.gamesPlayed} games with total score of ${totalScore}`);
    console.log(`⭐ Calculated ${historicalXP} XP from historical games`);
    console.log(`🏆 Set rank to: ${newRank}`);
  } catch (error) {
    console.error('❌ Error migrating stats:', error);
    throw error;
  }
};
